import { useEffect, useState } from "react";
import { Newspaper, ExternalLink, Play, X, ZoomIn, Quote } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  article: { label: "Articolo", Icon: Newspaper },
  video: { label: "Video", Icon: Play },
  clipping: { label: "Ritaglio stampa", Icon: Newspaper },
};

const PressCard = ({ item, onOpenClipping }) => {
  const meta = KIND_META[item.kind] || KIND_META.article;
  const { Icon } = meta;
  const isClipping = item.kind === "clipping";
  const clickable = isClipping || Boolean(item.url);

  const handleClick = () => {
    if (isClipping) onOpenClipping(item);
    else if (item.url) window.open(item.url, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      data-testid={`press-card-${item.id}`}
      className="group flex flex-col bg-white border-2 border-[#E5E1D8] hover:border-[#2E4F3B] rounded-3xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(0,0,0,0.10)]"
    >
      <button
        type="button"
        onClick={clickable ? handleClick : undefined}
        data-testid={`press-media-${item.id}`}
        className="relative block aspect-[16/10] overflow-hidden bg-[#1f2d22] text-left cursor-pointer"
      >
        {item.image_url && (
          <img
            src={item.image_url}
            alt={item.title}
            loading="lazy"
            className={`w-full h-full transition-transform duration-500 group-hover:scale-105 ${isClipping ? "object-cover object-top" : "object-cover"}`}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur text-[#1f2d22] text-[10px] font-bold uppercase tracking-wider px-3 py-1">
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
        {item.kind === "video" && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C15C3D] text-white shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
            </span>
          </span>
        )}
        {isClipping && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1">
            <ZoomIn className="w-3 h-3" /> Leggi il ritaglio
          </span>
        )}
      </button>

      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#C15C3D]">{item.source}</span>
          {item.date_label && <span className="text-[11px] text-[#9a9a9a]">· {item.date_label}</span>}
        </div>
        <h3 className="font-display text-lg sm:text-xl font-black text-[#1A1A1A] leading-snug mb-2 group-hover:text-[#2E4F3B] transition-colors">
          {item.title}
        </h3>
        {item.excerpt && <p className="text-sm text-[#5C5C5C] leading-relaxed flex-1 line-clamp-3">{item.excerpt}</p>}
        <div className="mt-5 flex items-center justify-between gap-3">
          {item.author && <span className="text-xs text-[#5C5C5C] italic truncate">di {item.author}</span>}
          <button
            type="button"
            onClick={handleClick}
            data-testid={`press-cta-${item.id}`}
            className="inline-flex items-center gap-2 text-sm font-bold text-[#2E4F3B] hover:gap-3 transition-all shrink-0 ml-auto"
          >
            {isClipping ? "Leggi il ritaglio" : item.kind === "video" ? "Guarda il video" : "Leggi l'articolo"}
            {isClipping ? <ZoomIn className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </article>
  );
};

export const SullaStampa = () => {
  const [items, setItems] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/press`).then((r) => r.json()).then((d) => setItems(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    if (lightbox) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  if (!items.length) return null;

  return (
    <section data-testid="home-sulla-stampa" className="relative py-20 sm:py-28 bg-white border-t border-[#E5E1D8]">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-[#C15C3D]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
              Sulla stampa
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] leading-[1.05] tracking-tight">
            Hanno parlato <span className="italic font-light text-[#2E4F3B]">di noi.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
            Giornali e media che hanno raccontato la nascita e le attività di Per Asciano.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => (
            <PressCard key={it.id} item={it} onOpenClipping={setLightbox} />
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[120] bg-black/85 flex flex-col p-4 sm:p-8"
          onClick={() => setLightbox(null)}
          data-testid="press-lightbox"
        >
          <div className="flex items-center justify-between text-white mb-3 max-w-5xl w-full mx-auto shrink-0">
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#E8C9A0] flex items-center gap-2">
                <Quote className="w-3.5 h-3.5" /> {lightbox.source}{lightbox.date_label ? ` · ${lightbox.date_label}` : ""}
              </div>
              <div className="font-display text-base sm:text-lg font-black truncate">{lightbox.title}</div>
            </div>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              data-testid="press-lightbox-close"
              className="shrink-0 ml-4 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-auto max-w-5xl w-full mx-auto flex items-start justify-center">
            <img
              src={lightbox.clipping_url || lightbox.image_url}
              alt={lightbox.title}
              onClick={(e) => e.stopPropagation()}
              className="rounded-xl bg-white shadow-2xl max-w-full h-auto"
            />
          </div>
        </div>
      )}
    </section>
  );
};
